import {
  RoomTypeDTO,
  RateDTO,
  BookerDTO,
  LocationDTO,
  AccountLookupDTO,
  SearchAvailabilityPropertyResultDTO,
  ReservationItemCreateDTO,
  ApiDropdown,
} from 'src/services/api/data-contracts';
import {
  IApiDropdownOption,
  ReservationItemService,
  IReservationItem,
  RoomTypeIdQuantityChecker,
  IVatSettings,
  IReservationItemUpgrade,
  ISearchRoom,
  CreditCard,
  Rate,
} from 'src/types/common';
import useDateHelperUtils from 'src/composables/dateHelperUtils';
import useUtils from 'src/composables/utils';
import useRateHelperUtils from 'src/composables/rateHelperUtils.ts';

const dateHelperUtils = useDateHelperUtils();
const rateHelperUtils = useRateHelperUtils();
const utils = useUtils();

export enum NavigationPage {
  None = 0,
  Search = 1,
  RoomsAndRates = 2,
  Checkout = 3,
}

export enum CreateBookingProcessStep {
  None = 1,
  RoomTypeSelection = 2,
  RatePlanSelection = 4,
  UpgradeSelection = 8,
  Occupancy = 16,
  Extras = 32,
  BookingSummary = 64,
  PersonalInformation = 128,
  Account = 256,
  AddressDetails = 512,
  PaymentDetails = 1024,
}

export enum ReservationPaymentType {
  StoreCard = 0, // tokenise the card
  PayByIndividualInvoice = 1,
  PayByVoucher = 2,
  PayByCompanyInvoice = 3,
  PayByPayStack = 4,
  PayByCard = 5, // pay now
}

export interface IAlternativeStay {
  fromDate: string;
  toDate: string;
  fromDateDisplay: string;
  numberOfNights: number;
  numberOfNightsDisplay: string;
  totalCost: number;
}

export interface INewBookingState {
  propertyId: number;
  languageCode: string;
  fromDate: string;
  toDate: string;
  promoCode?: string;
  hopGiftVoucherCode?: string;
  ratePlanCode?: string;
  rooms: ISearchRoom[];
  location?: LocationDTO;
  reservation: Reservation;
  paymentType: ReservationPaymentType;
  reservationItems: ReservationItem[];
  isApp: boolean;
  totalCostAtCheckout: number;
  totalTaxesAtCheckout: number;
}

export class Guest {
  constructor() {}
  firstName: string;
  lastName: string;
  get fullname(): string {
    return this.firstName + ' ' + this.lastName;
  }
  get isValid(): boolean {
    const isFirstValid =
      this.firstName != null &&
      this.firstName != undefined &&
      this.firstName != '';
    const isLastValid =
      this.lastName != null &&
      this.lastName != undefined &&
      this.lastName != '';
    return isFirstValid && isLastValid;
  }
}

export class Booker {
  constructor() {
    this.optInToReceiveUpdates = false;
  }
  firstName: string;
  lastName: string;
  telephoneNumber: string;
  emailAddress: string;
  street: string;
  postCode: string;
  country: IApiDropdownOption;
  optInToReceiveUpdates: boolean;
  get fullname(): string {
    return this.firstName + ' ' + this.lastName;
  }
  get arePersonalDetailsValid(): boolean {
    return (
      !!this.firstName &&
      !!this.lastName &&
      !!this.emailAddress &&
      !!this.telephoneNumber
    );
  }
  get areAddressDetailsValid(): boolean {
    return !!this.street && this.country != null && !!this.postCode;
  }

  get isValid(): boolean {
    return this.arePersonalDetailsValid && this.areAddressDetailsValid;
  }

  toDTO(): BookerDTO {
    return {
      FirstName: this.firstName,
      LastName: this.lastName,
      Email: this.emailAddress,
      Phone: this.telephoneNumber,
      Street: this.street,
      City: null,
      CountryId: this.country?.Id,
      StateOrCounty: null,
      OptInToReceiveUpdates: this.optInToReceiveUpdates,
      ZipCode: this.postCode,
    } as BookerDTO;
  }
}

export class Reservation {
  constructor() {
    this.booker = new Booker();
    this.creditCard = new CreditCard();
    this.agreeToTermsAndConditions = false;
    this.hasAcceptedCustomTermsAndConditions = false;
    this.hopGiftDiscountAmount = 0;
  }
  booker: Booker;
  password: string;

  creditCard: CreditCard;
  specialRequests: string;
  comment: string;

  hopGiftDiscountAmount: number;

  agreeToTermsAndConditions: boolean;

  hasAcceptedCustomTermsAndConditions: boolean;

  poNumber: string;
  company: AccountLookupDTO;

  get invoiceDetailsCaptured(): boolean {
    return this.poNumber != null && this.poNumber != '' && this.company != null;
  }
}

export class ReservationItem implements IReservationItem {
  constructor() {
    this.haveSeenServices = false;
    this.areServicesLoaded = false;
    this.hasSelectableServices = false;

    this.haveSeenUpgrades = false;
    this.availableUpgrades = [];
  }

  DateOfArrival: string;
  DateOfDeparture: string;

  ReservationItemId: number;

  roomId: string;
  roomNumber: number;

  NumberOfAdults: number;
  NumberOfChildren: number;
  NumberOfInfants: number;

  SetRate(rate: RateDTO): void {
    this.ratePlanDTO = rate;
    this.ratePlan = new Rate(rate);
  }

  get calculatedRoomCost(): number {
    if (this.ratePlan) {
      const totalRate = rateHelperUtils.calculateRoomRate(
        this.NumberOfAdults,
        this.NumberOfChildren,
        this.NumberOfInfants,
        this.ratePlan.BaseRate,
        this.ratePlan.BaseNumberOfAdults,
        this.ratePlan.CostPerRoom,
        this.ratePlan.CostPerAdult,
        this.ratePlan.CostPerChild,
        this.ratePlan.CostPerInfant,
        this.ratePlan.PerPersonCostPerAdult,
        this.ratePlan.PerPersonCostPerChild,
        this.ratePlan.PerPersonCostPerInfant,
      );

      return totalRate;
    }
    return 0;
  }

  get totalNumberOfGuests(): number {
    return this.NumberOfAdults + this.NumberOfChildren + this.NumberOfInfants;
  }

  roomConfiguration: ApiDropdown;

  leadGuest: Guest;

  roomHeader: string;
  get guestSummary(): string {
    return utils.getGuestSummaryText(
      this.NumberOfAdults,
      this.NumberOfChildren,
      this.NumberOfInfants,
    );
  }

  RatePlanId: number;
  ratePlanDTO: RateDTO;
  ratePlan: Rate;
  RoomTypeId: number;
  roomType: RoomTypeDTO;

  roomTypeQuantityChecker: RoomTypeIdQuantityChecker;
  VatSettings: IVatSettings;

  haveReviewedAllGuests: boolean;
  haveReviewedRoom: boolean;

  // True if they have seen the upgrades for this RI.
  haveSeenUpgrades: boolean;
  availableUpgrades: IReservationItemUpgrade[];

  clearUpgrades() {
    this.availableUpgrades.splice(0); // maintain reactivity.
  }

  // check if there are upgrades for this rate to any room type.
  checkForUpgrades() {
    const rpId = this.RatePlanId;

    if (this.roomTypeQuantityChecker == null) {
      this.roomTypeQuantityChecker = () => {
        return 0;
      };
    }

    if (this.roomType && this.roomType.Upgrades != null) {
      const ratePlanUpgrades = this.roomType.Upgrades.map((rtUpgrade) => {
        const availableQuantity = this.roomTypeQuantityChecker(
          rtUpgrade.RoomTypeId,
        );

        if (availableQuantity <= 0) {
          return null;
        }

        const matchingUpgradeRateDTO = rtUpgrade.AvailableRates.find((ar) => {
          return ar.RatePlanId == rpId;
        });
        if (matchingUpgradeRateDTO == null) {
          return null;
        } else {
          const utils = useUtils();

          const matchingUpgradeRate = new Rate(matchingUpgradeRateDTO);

          const isRoomTypeOccupancyValid = utils.checkIfOccupancyIsValid(
            this.NumberOfAdults,
            this.NumberOfChildren,
            this.NumberOfInfants,
            rtUpgrade.MaximumOccupancy,
            rtUpgrade.MinimumOccupancy,
            rtUpgrade.AdultMaximumOccupancy,
            rtUpgrade.ChildMaximumOccupancy,
          );

          let ratePlanOccupancyIsValid = true;
          if (matchingUpgradeRate.UseOccupancyForRatePlan) {
            ratePlanOccupancyIsValid = utils.checkIfOccupancyIsValid(
              this.NumberOfAdults,
              this.NumberOfChildren,
              this.NumberOfInfants,
              utils.getSmallestMaximumValue(
                matchingUpgradeRate.MaximumOccupancy,
                rtUpgrade.MaximumOccupancy,
              ),
              utils.getLargestMinimumValue(
                matchingUpgradeRate.MinimumOccupancy,
                rtUpgrade.MinimumOccupancy,
              ),
              utils.getSmallestMaximumValue(
                matchingUpgradeRate.AdultMaximumOccupancy,
                rtUpgrade.AdultMaximumOccupancy,
              ),
              utils.getSmallestMaximumValue(
                matchingUpgradeRate.ChildMaximumOccupancy,
                rtUpgrade.ChildMaximumOccupancy,
              ),
            );
          }

          if (isRoomTypeOccupancyValid && ratePlanOccupancyIsValid) {
            let cost = 0;
            const rateHelperUtils = useRateHelperUtils();
            cost = rateHelperUtils.calculateRoomRate(
              this.NumberOfAdults,
              this.NumberOfChildren,
              this.NumberOfInfants,
              matchingUpgradeRate.BaseRate,
              matchingUpgradeRate.BaseNumberOfAdults,
              matchingUpgradeRate.CostPerRoom,
              matchingUpgradeRate.CostPerAdult,
              matchingUpgradeRate.CostPerChild,
              matchingUpgradeRate.CostPerInfant,
              matchingUpgradeRate.PerPersonCostPerAdult,
              matchingUpgradeRate.PerPersonCostPerChild,
              matchingUpgradeRate.PerPersonCostPerInfant,
            );

            return {
              roomType: rtUpgrade,
              rate: matchingUpgradeRate,
              totalUpgradeCost: cost,
              totalCostDifference: cost - this.totalRoomCost,
            } as IReservationItemUpgrade;
          } else {
            return null;
          }
        }
      });
      this.availableUpgrades = ratePlanUpgrades.filter((ug) => {
        return ug != null;
      });

      if (!this.hasUpgrades) {
        this.haveSeenUpgrades = true;
      }
    }
  }

  get hasUpgrades(): boolean {
    const upgrades = this.availableUpgrades;
    return upgrades.length > 0;
  }

  // True if there are services available for selection
  hasSelectableServices: boolean;
  areServicesLoaded: boolean;
  haveSeenServices: boolean;
  Services: Array<ReservationItemService> = [];

  get hasServices(): boolean {
    return this.Services.length > 0;
  }

  get canEditServices(): boolean {
    let canEdit = false;
    if (this.Services.length == 0) {
      canEdit = true;
    } else {
      canEdit = this.Services.reduce((v, ris) => {
        return v || ris.canEdit || ris.justAdded;
      }, false);
    }

    return canEdit;
  }

  get canShowEditServices(): boolean {
    return (
      this.hasServices || (this.hasSelectableServices && this.canEditServices)
    );
  }

  getService(serviceId: number): ReservationItemService | null {
    const s = this.Services.filter((u) => {
      return u.ServiceId == serviceId;
    });

    return s.length > 0 ? s[0] : null;
  }

  get totalServiceCost(): number {
    const totalDays = dateHelperUtils.calculateNumberOfNights(
      this.DateOfArrival,
      this.DateOfDeparture,
    );
    const totalGuests = this.NumberOfAdults + this.NumberOfChildren;
    const cost = rateHelperUtils.calcuateTotalServiceCost(
      this.Services,
      totalDays,
      totalGuests,
    );
    return cost;
  }

  get totalRoomCost(): number {
    const cost = this.calculatedRoomCost;
    return cost;
  }

  get totalDiscountRoomCost(): number {
    let cost = 0;
    cost = rateHelperUtils.calculateRoomRate(
      this.NumberOfAdults,
      this.NumberOfChildren,
      this.NumberOfInfants,
      this.ratePlan.OriginalBaseRate,
      this.ratePlan.BaseNumberOfAdults,
      this.ratePlan.OriginalCostPerRoom,
      this.ratePlan.OriginalCostPerAdult,
      this.ratePlan.OriginalCostPerChild,
      this.ratePlan.OriginalCostPerInfant,
      this.ratePlan.OriginalPerPersonCostPerAdult,
      this.ratePlan.OriginalPerPersonCostPerChild,
      this.ratePlan.OriginalPerPersonCostPerInfant,
    );
    return cost;
  }

  totalCostForAnalytics: number;
  totalTaxesForAnalytics: number;

  get totalCost(): number {
    const total = this.totalRoomCost + this.totalServiceCost;
    if (!isNaN(total)) {
      this.totalCostForAnalytics = total;
    }
    return total;
  }

  get totalVatAmount(): number {
    const vatAmount = rateHelperUtils.calculateVatAmount(
      this.totalCost,
      this.VatSettings,
    );
    if (!isNaN(vatAmount)) {
      this.totalTaxesForAnalytics = vatAmount;
    }

    return vatAmount;
  }

  get totalPaymentAmount(): number {
    const totalCost = this.totalRoomCost + this.totalServiceCost;
    const paymentAmount = rateHelperUtils.calculatePolicyPaymentAmount(
      this.ratePlan,
      totalCost,
      this.DateOfArrival,
      this.NumberOfAdults,
    );

    return paymentAmount;
  }

  toDTO(): ReservationItemCreateDTO {
    return {
      DateFrom: this.DateOfArrival,
      DateTo: this.DateOfDeparture,
      RoomTypeId: this.roomType.RoomTypeId,
      RoomConfigurationDropdownOptionId: this.roomConfiguration?.Id,
      RatePlanId: this.ratePlan.RatePlanId,
      Promotion: this.ratePlan.Promotion,
      NumberOfAdults: this.NumberOfAdults,
      NumberOfChildren: this.NumberOfChildren,
      NumberOfInfants: this.NumberOfInfants,
      LeadGuest: this.leadGuest,
      Services: this.Services.map((rsi) => rsi.toReservationItemServiceDTO()),
      /*
       Only room cost because we compare the rates to check.
      */
      TotalCost: this.totalRoomCost,
    } as ReservationItemCreateDTO;
  }
}

export class PropertySearchResultDTO {
  constructor() {
    this.propertyId = null;
    this.isSearching = false;
    this.result = null;
  }
  propertyId: number | null;
  isSearching: boolean;
  result: SearchAvailabilityPropertyResultDTO | null;
}
